import { useEffect, useId, type ReactNode } from 'react';
import { Button, ButtonLink, Checkbox } from '@resideo/blueprint-oneportal';
import { closeIcon } from '../../assets/icons';
import styles from './FilterPanel.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FilterPanelChip {
  key: string;
  label: string;
  /** Accessible name of the remove button. Defaults to `Remove <label>`. */
  removeLabel?: string;
  onRemove: () => void;
}

export interface FilterPanelLabels {
  clearAll: string;
  close: string;
  selected: string;
  cancel: string;
  apply: string;
}

export interface FilterPanelProps {
  isOpen: boolean;
  title?: string;
  ariaLabel: string;
  chips: FilterPanelChip[];
  onClose: () => void;
  onApply: () => void;
  onClearAll: () => void;
  labels?: Partial<FilterPanelLabels>;
  children: ReactNode;
}

const DEFAULT_LABELS: FilterPanelLabels = {
  clearAll: 'Clear All',
  close: 'Close filter panel',
  selected: 'Selected',
  cancel: 'Cancel',
  apply: 'Apply',
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={`${styles.chevron} ${expanded ? styles.chevronUp : ''}`}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChipCloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4.5 4.5l7 7M11.5 4.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// ─── Panel ────────────────────────────────────────────────────────────────────

export function FilterPanel({
  isOpen,
  title = 'Filters',
  ariaLabel,
  chips,
  onClose,
  onApply,
  onClearAll,
  labels,
  children,
}: FilterPanelProps) {
  const text = { ...DEFAULT_LABELS, ...labels };

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />

      <div className={styles.panel} role="dialog" aria-modal="true" aria-label={ariaLabel}>
        <div className={styles.header}>
          <div className={styles.titleActions}>
            <h2 className={styles.title}>{title}</h2>
            {/* The Default design variant has no Clear All until something is selected. */}
            {chips.length > 0 && (
              <ButtonLink
                size="default"
                underline={false}
                className={styles.clearAllButton}
                onClick={onClearAll}
              >
                {text.clearAll}
              </ButtonLink>
            )}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={text.close}
          >
            <img src={closeIcon} width="20" height="20" alt="" aria-hidden="true" />
          </button>
        </div>

        <div className={styles.body}>
          {chips.length > 0 && (
            <div className={styles.selectedSection}>
              <div className={styles.selectedLabel}>{text.selected}</div>
              <div className={styles.selectedChips}>
                {chips.map(chip => (
                  <span key={chip.key} className={styles.chip}>
                    <span className={styles.chipLabel}>{chip.label}</span>
                    <button
                      type="button"
                      className={styles.chipRemove}
                      aria-label={chip.removeLabel ?? `Remove ${chip.label}`}
                      onClick={chip.onRemove}
                    >
                      <ChipCloseIcon />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {children}
        </div>

        <div className={styles.footer}>
          <Button variant="highlightTertiary" className={styles.cancelButton} onClick={onClose}>
            {text.cancel}
          </Button>
          <Button variant="primary" className={styles.applyButton} onClick={onApply}>
            {text.apply}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────

export interface FilterSectionProps {
  title: string;
  /** Number of selected options, shown as `Title (n)` when greater than 0. */
  count?: number;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
}

export function FilterSection({ title, count = 0, expanded, onToggle, children }: FilterSectionProps) {
  const contentId = useId();

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.sectionHeader}
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={onToggle}
      >
        <span className={styles.sectionTitle}>{count > 0 ? `${title} (${count})` : title}</span>
        <ChevronIcon expanded={expanded} />
      </button>

      {expanded && (
        <div id={contentId} className={styles.checkboxList}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Option ───────────────────────────────────────────────────────────────────

export interface FilterCheckboxProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

export function FilterCheckbox({ label, checked, onChange }: FilterCheckboxProps) {
  return (
    <div className={styles.checkboxRow}>
      <Checkbox checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

export function FilterEmptyNote({ children }: { children: ReactNode }) {
  return <span className={styles.emptyNote}>{children}</span>;
}
